import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const RecentIssuesSection = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecentIssues = async () => {
      try {
        const q = query(
          collection(db, "codeIssues"),
          orderBy("createdAt", "desc"),
          limit(10)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setIssues(data);
      } catch (err) {
        console.error("Error fetching issues:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRecentIssues();
  }, []);

  return (
    <SectionWrapper id="recent-issues">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Recent <Highlight>Code Issues</Highlight>
      </motion.h2>

      <Subtitle>
        Explore the latest issues posted by developers and start solving real
        challenges.
      </Subtitle>

      {loading ? (
        <LoadingText>Loading latest issues...</LoadingText>
      ) : issues.length === 0 ? (
        <LoadingText>No issues found 🚀</LoadingText>
      ) : (
        <>
          <HorizontalScroll>
            {issues.map((issue, i) => (
              <motion.div
                key={issue.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <IssueCard>
                  <CardTop>
                    <AvatarWrapper>
                      {issue.userImage ? (
                        <AvatarImage
                          src={issue.userImage}
                          alt={issue.postedBy}
                        />
                      ) : (
                        <User color="#fff" />
                      )}
                    </AvatarWrapper>

                    <UserInfo>
                      <span>{issue.postedBy || "Anonymous"}</span>
                      {issue.createdAt?.seconds ? (
                        <small>
                          {new Date(
                            issue.createdAt.seconds * 1000
                          ).toLocaleDateString()}
                        </small>
                      ) : (
                        <small>Just now</small>
                      )}
                    </UserInfo>
                  </CardTop>

                  <Title title={issue.title}>{issue.title}</Title>

                  <Description title={issue.description}>
                    {issue.description || "No description provided"}
                  </Description>

                  {issue.screenshot && (
                    <Screenshot src={issue.screenshot} alt="screenshot" />
                  )}

                  <Meta>
                    <Tag>{issue.type || "Free"}</Tag>
                    <Tag>{issue.technology || "Unknown"}</Tag>
                    {issue.difficulty && <Tag>{issue.difficulty}</Tag>}
                    <Tag>{(issue.status || "UNSOLVED").toLowerCase()}</Tag>
                    {issue.reward && <Small>{issue.reward} coins</Small>}
                  </Meta>

                  <ViewButton
                    onClick={() => navigate(`/code-issue/${issue.id}`)}
                  >
                    View Code Issue →
                  </ViewButton>
                </IssueCard>
              </motion.div>
            ))}
          </HorizontalScroll>

          <ViewAllButton onClick={() => navigate("/code-issues")}>
            View All Code Issues →
          </ViewAllButton>
        </>
      )}
    </SectionWrapper>
  );
};

export default RecentIssuesSection;

/* ---------------- Styled Components ---------------- */

const SectionWrapper = styled.section`
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 100px 20px;
  text-align: center;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #d2cde9;
  margin-top: 10px;
  margin-bottom: 40px;
`;

const LoadingText = styled.p`
  color: #c59fff;
  font-size: 1rem;
  margin-top: 20px;
`;

const HorizontalScroll = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding: 10px 10px 30px 10px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 6px;
  }
  // &::-webkit-scrollbar-thumb {
  //   background: linear-gradient(90deg, #9b5cff, #c59fff);
  //   border-radius: 10px;
  // }
`;

const IssueCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 20px;
  width: 300px;
  text-align: left;
  flex-shrink: 0;
  cursor: pointer;
  backdrop-filter: blur(6px);
  min-height: 250px; /* uniform height */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: 0.3s;

  &:hover {
    transform: translateY(-6px);
    border-color: #c59fff;
    box-shadow: 0 4px 15px rgba(155, 92, 255, 0.3);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AvatarWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #1b1b1b;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  span {
    font-weight: 600;
  }
  small {
    color: #aaa;
  }
`;

const Title = styled.h3`
  font-size: 1.1rem;
  margin: 12px 0 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Description = styled.p`
  font-size: 0.9rem;
  color: #ccc;
  margin-bottom: 10px;
  line-height: 1.5;
  max-height: 42px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Screenshot = styled.img`
  width: 100%;
  border-radius: 8px;
  margin: 10px 0;
  object-fit: cover;
  max-height: 140px;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const Tag = styled.span`
  background: rgba(155, 92, 255, 0.2);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.8rem;
  color: #c59fff;
`;

const Small = styled.small`
  margin-left: auto;
  color: #ccc;
  font-size: 0.8rem;
`;

const ViewButton = styled.button`
  margin-top: auto;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  border: none;
  color: white;
  font-weight: 600;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.9rem;
  align-self: flex-start;
  transition: all 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);
  }
`;

const ViewAllButton = styled.button`
  margin-top: 30px;
  background: transparent;
  border: 1px solid #9b5cff;
  color: #c59fff;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #9b5cff;
    color: white;
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);
  }
`;
